// Gap Assessment by MQS - wspolny helper API. Wlasny token, calkowicie niezalezny od
// AuthContext/iso_token reszty ISO Forge (patrz backend README - swiadoma decyzja z Fazy 0.2).
export const TOKEN_KEY = 'gap_mvp_token';
export const ROLE_KEY = 'gap_mvp_active_role_cache'; // tylko cache do szybkiego pierwszego renderu, backend jest zrodlem prawdy

export function getToken() { return localStorage.getItem(TOKEN_KEY); }
export function setToken(t) { localStorage.setItem(TOKEN_KEY, t); }
export function clearToken() { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(ROLE_KEY); }

export async function api(path, opts = {}) {
  const token = getToken();
  const res = await fetch(`/api${path}`, {
    ...opts,
    headers: {
      ...(opts.body && !(opts.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Blad ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

// Zwraca URL bez schematu/hosta - do wywolan spoza api() (np. streaming SSE z FormData recznie).
export function apiUrl(path) { return `/api${path}`; }

// Wspolny helper pobierania plikow binarnych (raport PDF, oryginalny plik zrodlowy, zip pakietu) -
// fetch+blob+link-click, zamiast zwyklego <a href> (potrzebny naglowek Authorization, ktorego link
// w HTML nie moze wyslac). Wydzielone 06.08 - wczesniej ta sama logika byla duplikowana wprost w
// ApprovalsView.jsx, teraz reuzywana tez w ClientSpaceView (zakladki Dokumenty/Historia).
export async function downloadFile(path, fallbackFilename) {
  const resp = await fetch(apiUrl(path), { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Blad ${resp.status}`);
  }
  const disposition = resp.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match ? decodeURIComponent(match[1]) : (fallbackFilename || 'plik');
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255, g = parseInt(hex.slice(3, 5), 16) / 255, b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return [h, s, l];
}
function hslToHex(h, s, l) {
  const hue2rgb = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1 / 6) return p + (q - p) * 6 * t; if (t < 1 / 2) return q; if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6; return p; };
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3); g = hue2rgb(p, q, h); b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (v) => Math.round(v * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Branding na zywo (pkt 12 architektury, Faza E) - kolor marki z accountSettings.branding.color
// realnie nadpisuje --accent w CSS w czasie dzialania frontendu (nie tylko podglad w karcie).
// --accent-soft liczony przez color-mix (Chrome 111+), --accent-ink przez prosta luminancje
// zeby tekst na przyciskach zostal czytelny niezaleznie od jasnosci wybranego koloru.
// 09.08 - "Semafor": dostosowujemy jasnosc wybranego przez firme koloru do aktualnego motywu
// (ten sam odcien, ale jasniejszy w ciemnym motywie / ciemniejszy w jasnym) - bez tego jeden,
// stale zapisany hex zaczal wygladac zle w jednym z dwoch motywow (za ciemny na ciemnym tle
// albo za jasny/blady na jasnym), zamiast po prostu dopasowywac sie jak reszta palety.
export function applyAccentColor(hex) {
  const root = document.querySelector('.ga-root');
  if (!root || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return;
  const isDark = root.getAttribute('data-theme') === 'dark'
    || (root.getAttribute('data-theme') !== 'light' && window.matchMedia?.('(prefers-color-scheme: dark)').matches !== false);
  const [h, s] = hexToHsl(hex);
  const adjusted = hslToHex(h, Math.max(s, 0.35), isDark ? 0.56 : 0.32);
  root.style.setProperty('--accent', adjusted);
  root.style.setProperty('--accent-soft', `color-mix(in srgb, ${adjusted} 16%, var(--paper-raised))`);
  const r = parseInt(adjusted.slice(1, 3), 16), g = parseInt(adjusted.slice(3, 5), 16), b = parseInt(adjusted.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  root.style.setProperty('--accent-ink', luminance > 0.6 ? '#111827' : '#FFFFFF');
}

export const NORMS = [
  { id: '9001', label: 'ISO 9001' }, { id: '14001', label: 'ISO 14001' }, { id: '45001', label: 'ISO 45001' },
  { id: '27001', label: 'ISO 27001' }, { id: '22301', label: 'ISO 22301' }, { id: 'nis2', label: 'NIS2/UKSC' },
  { id: '16949', label: 'IATF 16949' }, { id: 'vda63', label: 'VDA 6.3' }, { id: '42001', label: 'ISO 42001' },
  { id: 'aqap2110', label: 'AQAP 2110' }, { id: 'aqap2210', label: 'AQAP 2210' }, { id: 'aqap2131', label: 'AQAP 2131' },
  { id: 'gdp', label: 'GDP' },
];
export const normLabel = (id) => NORMS.find(n => n.id === id)?.label || id;

// Szablony branzowe (pkt 9 z 13-punktowej listy) - sugerowany zestaw norm per branza, punkt
// wyjscia, nie wymog (silnik i tak nie ocenia wzgledem sztywnego szablonu - patrz koncepcja klauzula 4).
export const INDUSTRY_TEMPLATES = [
  { industry: 'Transport i logistyka', norms: ['9001', '45001'] },
  { industry: 'Produkcja przemysłowa', norms: ['9001', '14001', '45001'] },
  { industry: 'Automotive / motoryzacja', norms: ['9001', '16949'] },
  { industry: 'IT / oprogramowanie', norms: ['9001', '27001'] },
  { industry: 'Usługi finansowe', norms: ['27001', '22301'] },
  { industry: 'Budownictwo', norms: ['9001', '14001', '45001'] },
  { industry: 'Ochrona zdrowia', norms: ['9001', '27001'] },
  { industry: 'Energetyka', norms: ['9001', '14001', '45001', '27001'] },
  { industry: 'Dystrybucja farmaceutyczna', norms: ['gdp', '9001'] },
  { industry: 'Obronność / NATO', norms: ['aqap2110', '9001'] },
  { industry: 'Telekomunikacja', norms: ['9001', '27001', '22301'] },
  { industry: 'Handel detaliczny', norms: ['9001'] },
  { industry: 'Sektor publiczny', norms: ['nis2', '27001'] },
  { industry: 'Inna / nieokreślona', norms: [] },
];
